import { type ReactNode } from 'react';

import { DamageTooltip } from 'components/damages/DamageTooltip';
import { type AtlasGqlHorizonDamageObservation } from 'types/atlas-graphql';
import { type OSD4ViewerWithSVG } from 'utils/openseadragon4';

import {
  DebugInferenceBoundaryPolygon,
  InferenceBoundaryPolygon,
  ObservationPolygon,
} from './ImageViewer.style';
import { OverlayTooltip } from './OverlayTooltip';

type BoundaryOverlay = {
  coordinates: [number, number][];
  detectionType?: string;
  tooltip?: string;
};

type ObservationOverlay = AtlasGqlHorizonDamageObservation & {
  selected: boolean;
};

type Props = {
  assetOverlayRender?: (arg?: OSD4ViewerWithSVG) => JSX.Element | null;
  deltaTMeasurementRender?: (arg?: OSD4ViewerWithSVG) => JSX.Element | null;
  onObservationClick: (observationId?: string) => void;
  hasColorCodedObservations?: boolean;
  imagePaneBox?: DOMRect;
  inferenceBoundaries: BoundaryOverlay[];
  isNewFullSizeImageViewer?: boolean;
  measurementsRender?: (arg?: OSD4ViewerWithSVG) => JSX.Element | null;
  observations: ObservationOverlay[];
  panelDetectionBoundaries: BoundaryOverlay[];
  render: (arg?: OSD4ViewerWithSVG) => JSX.Element | null;
  showAssetOverlay?: boolean;
  showDeltaTMeasurement?: boolean;
  showInferenceOverlay?: boolean;
  showMeasurements?: boolean;
  showTemperatures?: boolean;
  temperaturesRender?: (arg?: OSD4ViewerWithSVG) => JSX.Element | null;
  viewer: OSD4ViewerWithSVG;
};

const getOverlayTooltipTitle = (tooltip?: string): ReactNode => tooltip;

const getBoundaryKey = (boundary: BoundaryOverlay) => {
  const coordinatesKey = boundary.coordinates.map(([x, y]) => `${x},${y}`).join(';');
  return `${boundary.detectionType ?? 'unknown'}-${coordinatesKey}`;
};

export const ImageSvgOverlay = ({
  assetOverlayRender,
  deltaTMeasurementRender,
  onObservationClick,
  hasColorCodedObservations,
  imagePaneBox,
  inferenceBoundaries,
  isNewFullSizeImageViewer = false,
  measurementsRender,
  observations,
  panelDetectionBoundaries,
  render,
  showAssetOverlay = false,
  showDeltaTMeasurement = false,
  showInferenceOverlay = false,
  showMeasurements = false,
  showTemperatures = false,
  temperaturesRender,
  viewer,
}: Props) => {
  return (
    <>
      {render(viewer)}
      {showMeasurements && measurementsRender?.(viewer)}
      {showTemperatures && temperaturesRender?.(viewer)}
      {showAssetOverlay && assetOverlayRender?.(viewer)}
      {showDeltaTMeasurement && deltaTMeasurementRender?.(viewer)}
      {showInferenceOverlay && (
        <g className="inference-boundary-group">
          {inferenceBoundaries.map((boundary, index) => {
            const boundaryKey = getBoundaryKey(boundary);

            return (
              <OverlayTooltip
                key={`inference-boundary-tooltip-${boundaryKey}`}
                imagePaneBox={imagePaneBox}
                isNewFullSizeImageViewer={isNewFullSizeImageViewer}
                title={getOverlayTooltipTitle(boundary.tooltip)}
              >
                <InferenceBoundaryPolygon
                  aria-label={boundary.tooltip ?? boundary.detectionType ?? 'AI Boundary'}
                  className="inference-boundary-polygon"
                  data-inferenceboundaryindex={index}
                  key={`inference-boundary-${boundaryKey}`}
                  coordinates={boundary.coordinates}
                />
              </OverlayTooltip>
            );
          })}
        </g>
      )}
      {showInferenceOverlay && panelDetectionBoundaries.length > 0 && (
        <g className="debug-inference-boundary-group">
          {panelDetectionBoundaries.map(boundary => {
            const boundaryKey = getBoundaryKey(boundary);

            return (
              <OverlayTooltip
                key={`debug-inference-boundary-tooltip-${boundaryKey}`}
                imagePaneBox={imagePaneBox}
                isNewFullSizeImageViewer={isNewFullSizeImageViewer}
                title={getOverlayTooltipTitle(boundary.tooltip)}
              >
                <DebugInferenceBoundaryPolygon
                  aria-label={boundary.tooltip ?? boundary.detectionType ?? 'Panel Detection'}
                  className="debug-inference-boundary-polygon"
                  key={`debug-inference-boundary-${boundaryKey}`}
                  coordinates={boundary.coordinates}
                />
              </OverlayTooltip>
            );
          })}
        </g>
      )}
      <g className="observation-group">
        {observations.map((observation, index) => (
          <DamageTooltip
            id={observation.id}
            key={observation.id ? `${observation.id}_tip` : `observation-tooltip-${index}`}
            onClick={() => onObservationClick(observation.id)}
            isHorizonDamage={true}
            isNewFullSizeImageViewer={isNewFullSizeImageViewer}
            imagePaneBox={imagePaneBox}
          >
            <ObservationPolygon
              className="observation-polygon"
              data-observationid={observation.id}
              key={observation.id}
              selected={observation.selected}
              coordinates={observation.coordinates}
              severity={observation.attrs?.['Severity']}
              hasColorCodedObservations={hasColorCodedObservations}
            />
          </DamageTooltip>
        ))}
      </g>
    </>
  );
};
